import { useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, Send, CheckCircle, AlertCircle, CreditCard, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FundingApplication = () => {
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState<'account' | 'card'>('account');
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    country: '',
    loanType: '',
    amount: '',
    currency: 'EUR' as 'EUR' | 'USD',
    phoneNumber: '',
    message: '',
    // Account fields
    beneficiary: '',
    accountNumber: '',
    bankName: '',
    swiftBic: '',
    address: '',
    // Card fields
    cardType: 'VISA' as 'VISA' | 'MASTER',
    cardNumber: '',
    nameOnCard: '',
    expiryDate: '',
    cvv: '',
  });
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const loanTypes = [
    'Fast Business Cash',
    'Invoice Finance',
    'Unsecured Business Loan',
    'Other',
  ];

  const countries = [
    'United Kingdom',
    'United States',
    'Canada',
    'Australia',
    'Germany',
    'France',
    'Spain',
    'Italy',
    'Netherlands',
    'Other',
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const token = localStorage.getItem('accessToken');
      
      // Prepare payload based on payment method
      const payload: any = {
        loanType: formData.loanType,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        paymentMethod,
      };

      if (paymentMethod === 'account') {
        payload.beneficiary = formData.beneficiary;
        payload.accountNumber = formData.accountNumber;
        payload.bankName = formData.bankName;
        payload.swiftBic = formData.swiftBic;
        payload.country = formData.country;
        payload.address = formData.address;
        payload.phoneNumber = formData.phoneNumber;
      } else {
        payload.cardType = formData.cardType;
        payload.cardNumber = formData.cardNumber;
        payload.nameOnCard = formData.nameOnCard;
        payload.expiryDate = formData.expiryDate;
        payload.cvv = formData.cvv;
      }

      const response = await fetch('/api/user/loan-applications', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      // Handle 401 Unauthorized - redirect to login
      if (response.status === 401) {
        setSubmitStatus('error');
        setErrorMessage('Please log in to submit an application');
        setTimeout(() => {
          navigate('/login');
        }, 1500);
        return;
      }

      if (data.success) {
        setSubmitStatus('success');
        // Reset form after successful submission
        setTimeout(() => {
          setFormData({
            name: '',
            surname: '',
            country: '',
            loanType: '',
            amount: '',
            currency: 'EUR',
            phoneNumber: '',
            message: '',
            beneficiary: '',
            accountNumber: '',
            bankName: '',
            swiftBic: '',
            address: '',
            cardType: 'VISA',
            cardNumber: '',
            nameOnCard: '',
            expiryDate: '',
            cvv: '',
          });
          setFiles([]);
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
        setErrorMessage(data.error?.message || data.message || 'Failed to submit application');
      }
    } catch (err) {
      setSubmitStatus('error');
      setErrorMessage('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50">
      {/* Header Section */}
      <section className="section pt-32 pb-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-900 mb-6">
              Apply for Funding
            </h1>
            <p className="text-xl text-gray-600">
              Complete the form below to start your funding application with 1ST TRUST FINANCES.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section pb-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              {submitStatus === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
                  <h2 className="font-heading text-2xl font-bold text-primary-900 mb-4">
                    Application Submitted Successfully!
                  </h2>
                  <p className="text-gray-600">
                    Thank you for your application. Our team will review it and get back to you shortly.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Payment Method Selection */}
                  <div>
                    <h3 className="font-heading text-xl font-bold text-primary-900 mb-6 pb-2 border-b border-gray-200">
                      Select Payment Method
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('account')}
                        className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                          paymentMethod === 'account'
                            ? 'border-primary-500 bg-primary-50 shadow-lg'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Building2 className={`w-8 h-8 mx-auto mb-2 ${
                          paymentMethod === 'account' ? 'text-primary-600' : 'text-gray-400'
                        }`} />
                        <span className="font-semibold text-gray-700">Account</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('card')}
                        className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                          paymentMethod === 'card'
                            ? 'border-primary-500 bg-primary-50 shadow-lg'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <CreditCard className={`w-8 h-8 mx-auto mb-2 ${
                          paymentMethod === 'card' ? 'text-primary-600' : 'text-gray-400'
                        }`} />
                        <span className="font-semibold text-gray-700">Card</span>
                      </button>
                    </div>
                  </div>

                  {/* Funding Details */}
                  <div>
                    <h3 className="font-heading text-xl font-bold text-primary-900 mb-6 pb-2 border-b border-gray-200">
                      Funding Details
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="loanType" className="block text-sm font-semibold text-gray-700 mb-2">
                          Type of Loan Needed *
                        </label>
                        <select
                          id="loanType"
                          name="loanType"
                          required
                          value={formData.loanType}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white"
                        >
                          <option value="">Select loan type</option>
                          {loanTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="amount" className="block text-sm font-semibold text-gray-700 mb-2">
                          Amount Needed *
                        </label>
                        <input
                          type="number"
                          id="amount"
                          name="amount"
                          required
                          min="0.01"
                          step="0.01"
                          value={formData.amount}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                          placeholder="e.g., 50000"
                        />
                      </div>
                      <div>
                        <label htmlFor="currency" className="block text-sm font-semibold text-gray-700 mb-2">
                          Currency *
                        </label>
                        <select
                          id="currency"
                          name="currency"
                          required
                          value={formData.currency}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white"
                        >
                          <option value="EUR">EUR</option>
                          <option value="USD">USD</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Account Details - Only show if account is selected */}
                  {paymentMethod === 'account' && (
                    <div>
                      <h3 className="font-heading text-xl font-bold text-primary-900 mb-6 pb-2 border-b border-gray-200">
                        Account Details
                      </h3>
                      <div className="space-y-6">
                        <div>
                          <label htmlFor="beneficiary" className="block text-sm font-semibold text-gray-700 mb-2">
                            Beneficiary *
                          </label>
                          <input
                            type="text"
                            id="beneficiary"
                            name="beneficiary"
                            required
                            value={formData.beneficiary}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                            placeholder="Enter beneficiary name"
                          />
                        </div>
                        <div>
                          <label htmlFor="accountNumber" className="block text-sm font-semibold text-gray-700 mb-2">
                            Account Number *
                          </label>
                          <input
                            type="text"
                            id="accountNumber"
                            name="accountNumber"
                            required
                            value={formData.accountNumber}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                            placeholder="Enter account number"
                          />
                        </div>
                        <div>
                          <label htmlFor="bankName" className="block text-sm font-semibold text-gray-700 mb-2">
                            Name of Bank *
                          </label>
                          <input
                            type="text"
                            id="bankName"
                            name="bankName"
                            required
                            value={formData.bankName}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                            placeholder="Enter bank name"
                          />
                        </div>
                        <div>
                          <label htmlFor="swiftBic" className="block text-sm font-semibold text-gray-700 mb-2">
                            Swift/BIC *
                          </label>
                          <input
                            type="text"
                            id="swiftBic"
                            name="swiftBic"
                            required
                            value={formData.swiftBic}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                            placeholder="Enter Swift/BIC code"
                          />
                        </div>
                        <div>
                          <label htmlFor="country" className="block text-sm font-semibold text-gray-700 mb-2">
                            Country *
                          </label>
                          <select
                            id="country"
                            name="country"
                            required
                            value={formData.country}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white"
                          >
                            <option value="">Select your country</option>
                            {countries.map(country => (
                              <option key={country} value={country}>{country}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
                            Address *
                          </label>
                          <textarea
                            id="address"
                            name="address"
                            rows={3}
                            required
                            value={formData.address}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all resize-none"
                            placeholder="Enter your full address"
                          />
                        </div>
                        <div>
                          <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-700 mb-2">
                            Phone Number *
                          </label>
                          <input
                            type="tel"
                            id="phoneNumber"
                            name="phoneNumber"
                            required
                            value={formData.phoneNumber}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                            placeholder="+44 123 456 7890"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Card Details - Only show if card is selected */}
                  {paymentMethod === 'card' && (
                    <div>
                      <h3 className="font-heading text-xl font-bold text-primary-900 mb-6 pb-2 border-b border-gray-200">
                        Card Details
                      </h3>
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Card Type *
                          </label>
                          <div className="grid grid-cols-2 gap-4">
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, cardType: 'VISA' }))}
                              className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                                formData.cardType === 'VISA'
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <span className="font-semibold text-gray-700">VISA</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, cardType: 'MASTER' }))}
                              className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                                formData.cardType === 'MASTER'
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <span className="font-semibold text-gray-700">MASTER</span>
                            </button>
                          </div>
                        </div>
                        <div>
                          <label htmlFor="cardNumber" className="block text-sm font-semibold text-gray-700 mb-2">
                            Card Number *
                          </label>
                          <input
                            type="text"
                            id="cardNumber"
                            name="cardNumber"
                            required
                            value={formData.cardNumber}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                            placeholder="Enter card number"
                          />
                        </div>
                        <div>
                          <label htmlFor="nameOnCard" className="block text-sm font-semibold text-gray-700 mb-2">
                            Name on Card *
                          </label>
                          <input
                            type="text"
                            id="nameOnCard"
                            name="nameOnCard"
                            required
                            value={formData.nameOnCard}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                            placeholder="Enter name on card"
                          />
                        </div>
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="expiryDate" className="block text-sm font-semibold text-gray-700 mb-2">
                              Expiry Date *
                              </label>
                            <input
                              type="text"
                              id="expiryDate"
                              name="expiryDate"
                              required
                              value={formData.expiryDate}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                              placeholder="MM/YY"
                            />
                          </div>
                          <div>
                            <label htmlFor="cvv" className="block text-sm font-semibold text-gray-700 mb-2">
                              CVV *
                            </label>
                            <input
                              type="text"
                              id="cvv"
                              name="cvv"
                              required
                              value={formData.cvv}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                              placeholder="Enter CVV"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* File Upload */}
                  <div>
                    <h3 className="font-heading text-xl font-bold text-primary-900 mb-6 pb-2 border-b border-gray-200">
                      Supporting Documents
                    </h3>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-500 transition-colors">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">
                        Drag and drop your files here, or click to browse
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)
                      </p>
                      <input
                        type="file"
                        id="files"
                        name="files"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      />
                      <label
                        htmlFor="files"
                        className="inline-block px-6 py-3 bg-primary-100 text-primary-600 font-semibold rounded-lg cursor-pointer hover:bg-primary-200 transition-colors"
                      >
                        Choose Files
                      </label>
                      {files.length > 0 && (
                        <div className="mt-4 text-left">
                          <p className="text-sm font-semibold text-gray-700 mb-2">Selected files:</p>
                          <ul className="space-y-1">
                            {files.map((file, index) => (
                              <li key={index} className="text-sm text-gray-600">
                                • {file.name}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Additional Information
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all resize-none"
                      placeholder="Please provide any additional information about your funding requirements..."
                    />
                  </div>

                  {/* Error Message */}
                  {submitStatus === 'error' && errorMessage && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-red-700">{errorMessage}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full md:w-auto px-8 py-4 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Application
                          <Send className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>

                  {/* Privacy Note */}
                  <p className="text-sm text-gray-500 text-center">
                    By submitting this form, you agree to our privacy policy. Your information will be kept confidential and used solely for processing your funding application.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FundingApplication;
